import { Text, AppShellMain, Box, Flex, AppShell, Select, SegmentedControl, ActionIcon, Tooltip } from "@mantine/core"
import { IconCalendar, IconRefresh } from "@tabler/icons-react"
import { useState } from "react"
import React from 'react'
import { LEAGUE, getSeasons } from "../models/NBA"
import { Season } from "../client"
import { NBAInsightsHeader } from "../components/Header/Header"
import LeagueLeaders, { SimpleStat } from "../components/HomeComponents/LeagueLeaders"
import LeagueStandings from "../components/HomeComponents/LeagueStandings"
import ScheduleGames from "../components/HomeComponents/ScheduleGames"
import { useLeague } from "../context/LeagueContext"

const Dashboard: React.FC = () => {
  const { league } = useLeague()
  const [nbaLeadersSeason, setNbaLeadersSeason] = useState<Season>(getSeasons(LEAGUE.NBA, "LEADERS")[0])
  const [wnbaLeadersSeason, setWnbaLeadersSeason] = useState<Season>(getSeasons(LEAGUE.WNBA, "LEADERS")[0])
  const [nbaStandingsSeason, setNbaStandingsSeason] = useState<Season>(getSeasons(LEAGUE.NBA, "STANDINGS")[0])
  const [wnbaStandingsSeason, setWnbaStandingsSeason] = useState<Season>(getSeasons(LEAGUE.WNBA, "STANDINGS")[0])
  const [selectedStat, setSelectedStat] = useState<SimpleStat>("PTS")
  const [refreshKey, setRefreshKey] = useState<number>(0)

  const leadersSeason = league === LEAGUE.NBA ? nbaLeadersSeason : wnbaLeadersSeason
  const standingsSeason = league === LEAGUE.NBA ? nbaStandingsSeason : wnbaStandingsSeason

  const seasonSelectStyles = {
    input: {
      backgroundColor: '#222222',
      borderColor: '#3a3a3a',
      color: '#ffffff',
      fontWeight: 600,
      fontSize: '12px',
      cursor: 'pointer',
    },
    dropdown: {
      backgroundColor: '#1c1c1c',
      borderColor: '#333333',
    },
    option: {
      fontSize: '12px',
      borderRadius: '8px',
    },
  }

  return (
    <AppShell
      padding={"md"}
      header={{ height: 64 }}
    > 
      <NBAInsightsHeader />
      <AppShellMain>
        <ScheduleGames league={league} />
        {/* League Leaders Header with Inline Season Dropdown, Stat Selector, and Refresh Button */}
        <Flex justify="space-between" align="center" wrap="wrap" gap="sm" px={{ base: 'xs', sm: 'md' }} mb={6}>
          <Flex align="center" gap="sm" justify={{ base: 'space-between', sm: 'flex-start' }} w={{ base: '100%', sm: 'auto' }}>
            <Text fz={"h3"} fw={"bold"}>League Leaders</Text>
            <Select
              size="xs"
              w={115}
              radius="xl"
              leftSection={<IconCalendar size={13} style={{ opacity: 0.7 }} />}
              data={getSeasons(league, "LEADERS")}
              value={leadersSeason}
              onChange={(value) => {
                if (value) {
                  if (league === LEAGUE.NBA) {
                    setNbaLeadersSeason(value as Season)
                  } else {
                    setWnbaLeadersSeason(value as Season)
                  }
                }
              }}
              allowDeselect={false}
              comboboxProps={{ transitionProps: { transition: 'pop', duration: 150 }, shadow: 'md' }}
              styles={seasonSelectStyles}
            />
          </Flex>

          <Flex align="center" gap="xs" justify={{ base: 'center', sm: 'flex-start' }} w={{ base: '100%', sm: 'auto' }}>
            <SegmentedControl
              size="xs"
              radius="xl"
              value={selectedStat}
              onChange={(val) => setSelectedStat(val as SimpleStat)}
              data={[
                { label: 'PTS', value: 'PTS' },
                { label: 'AST', value: 'AST' },
                { label: 'REB', value: 'REB' },
                { label: 'STL', value: 'STL' },
                { label: 'BLK', value: 'BLK' },
              ]}
              styles={{
                root: {
                  backgroundColor: '#202020',
                  border: '1px solid #383838',
                  padding: 2,
                },
                label: {
                  fontWeight: 700,
                  fontSize: 12,
                  padding: '4px 12px',
                },
              }}
            />
            <Tooltip label="Refresh leaders" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                radius="xl"
                onClick={() => setRefreshKey((prev) => prev + 1)}
                aria-label="Refresh leaders"
                style={{ border: '1px solid #383838', backgroundColor: '#202020' }}
              >
                <IconRefresh size={15} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Flex>

        {/* Single-Stat League Leaders Widget */}
        <Box p={10} w={"100%"}>
          <LeagueLeaders
            league={league}
            stat={selectedStat}
            season={leadersSeason}
            refreshKey={refreshKey}
          />
        </Box>

        {/* League Standings Header with Inline Season Dropdown */}
        <Flex align="center" justify={{ base: 'space-between', sm: 'flex-start' }} gap="sm" px={{ base: 'xs', sm: 'md' }} mt={16} mb={4}>
          <Text fz={"h3"} fw={"bold"}>League Standings</Text>
          <Select
            size="xs"
            w={115}
            radius="xl"
            leftSection={<IconCalendar size={13} style={{ opacity: 0.7 }} />}
            data={getSeasons(league, "STANDINGS")}
            value={standingsSeason}
            onChange={(value) => {
              if (value) {
                if (league === LEAGUE.NBA) {
                  setNbaStandingsSeason(value as Season)
                } else {
                  setWnbaStandingsSeason(value as Season)
                }
              }
            }}
            allowDeselect={false}
            comboboxProps={{ transitionProps: { transition: 'pop', duration: 150 }, shadow: 'md' }}
            styles={seasonSelectStyles}
          />
        </Flex>

        {/* League Standings Widget */}
        <Box p={10} w={"100%"}>
          <LeagueStandings league={league} season={standingsSeason} />
        </Box>

      </AppShellMain>
    </AppShell>
  )
}

export default Dashboard
